import React from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LogOut } from 'lucide-react';

export default function LockedLayout() {
    const { logout, user } = useAuth();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            {/* Minimal Header */}
            <header className="bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center shadow-sm z-10">
                <div className="flex items-center gap-3">
                    {/* Logo */}
                    <div className="flex-shrink-0">
                        <img
                            src="/logo-sidebar.png"
                            alt="SyncLyst"
                            className="h-10 w-auto object-contain"
                            style={{ maxWidth: '140px' }}
                            onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const fallback = target.nextElementSibling as HTMLElement;
                                if (fallback) fallback.style.display = 'flex';
                            }}
                        />
                        {/* Fallback Icon */}
                        <div
                            className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg items-center justify-center shadow-lg"
                            style={{ display: 'none' }}
                        >
                            <span className="text-white font-bold text-lg">SL</span>
                        </div>
                    </div>
                    <div>
                        <h1 className="text-xl text-brand-glow">SyncLyst</h1>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                            Account Expired
                        </span>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    <div className="text-sm text-gray-600 hidden sm:block">
                        Logged in as <span className="font-medium text-gray-900">{user?.email}</span>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                    </button>
                </div>
            </header>

            {/* Main Content - No Sidebar, No Background Fetches */}
            <main className="flex-1 overflow-auto p-4 md:p-8">
                <div className="max-w-7xl mx-auto">
                    {/* Persistent Warning Banner */}
                    <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-8 rounded-r shadow-sm">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-700">
                                    <span className="font-bold">Access Suspended:</span> Your subscription has expired. Please update your payment method below to restore full access to your account.
                                </p>
                            </div>
                        </div>
                    </div>

                    {user?.role === 'owner' || user?.role === 'admin' ? (
                        <Outlet />
                    ) : (
                        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                            <h2 className="text-xl font-semibold text-gray-900 mb-2">Subscription required</h2>
                            <p className="text-gray-600">
                                Your company account is currently suspended due to an expired subscription. Please contact the account owner to renew access.
                            </p>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
